import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useActiveAdsForPlacement, AdBooking } from '@/hooks/useAdPlacements';
import { VerifiedBadge } from '@/components/tools/VerifiedBadge';

interface SponsoredToolCardProps {
  booking: AdBooking;
}

function SponsoredToolCard({ booking }: SponsoredToolCardProps) {
  const tool = booking.tools;
  if (!tool) return null;

  return (
    <Card className="relative overflow-hidden border-dashed">
      <Badge 
        variant="secondary" 
        className="absolute top-2 right-2 text-[10px] bg-muted/80"
      >
        Sponsored
      </Badge>
      <CardContent className="p-4">
        <Link to={`/tool/${tool.slug}`} className="flex items-start gap-3 group">
          {tool.logo_url ? (
            <img 
              src={tool.logo_url} 
              alt={tool.name}
              className="w-10 h-10 rounded-lg object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <span className="text-lg font-bold text-primary">
                {tool.name.charAt(0)}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h4 className="font-medium group-hover:text-primary transition-colors line-clamp-1">
                {tool.name}
              </h4>
              {tool.is_verified && (
                <VerifiedBadge tier={(tool.verification_tier as 'basic' | 'pro' | 'premium') || 'basic'} size="sm" />
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {tool.short_description}
            </p>
          </div>
          <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        </Link>
      </CardContent>
    </Card>
  );
}

interface FeaturedToolsBannerProps {
  placementSlug: string;
  title?: string;
  className?: string;
  gridClassName?: string;
}

export function FeaturedToolsBanner({ placementSlug, title, className, gridClassName = 'grid-cols-1 md:grid-cols-3' }: FeaturedToolsBannerProps) {
  const { data: ads, isLoading } = useActiveAdsForPlacement(placementSlug);

  if (isLoading) {
    return (
      <div className={className}>
        <div className={`grid ${gridClassName} gap-4`}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!ads || ads.length === 0) return null;

  return (
    <div className={className} aria-label={`${placementSlug} sponsored tools`}>
      {title && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <Badge variant="outline" className="text-xs">Featured</Badge>
        </div>
      )}
      <div className={`grid ${gridClassName} gap-4`}>
        {ads.map((ad) => (
          <SponsoredToolCard key={ad.id} booking={ad} />
        ))}
      </div>
    </div>
  );
}

interface SponsoredAlternativesProps {
  currentToolId: string;
}

export function SponsoredAlternatives({ currentToolId }: SponsoredAlternativesProps) {
  const { data: ads } = useActiveAdsForPlacement('tool-alternatives');
  
  // Filter out the current tool
  const filteredAds = ads?.filter(ad => ad.tool_id !== currentToolId);
  
  if (!filteredAds || filteredAds.length === 0) return null;

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
        Sponsored Alternatives
      </h4>
      <div className="space-y-3">
        {filteredAds.slice(0, 3).map((ad) => (
          <SponsoredToolCard key={ad.id} booking={ad} />
        ))}
      </div>
    </div>
  );
}
